import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import type { Decks } from "@cardheim/database";
import type { Signal } from "@preact-signals/safe-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Selectable } from "kysely";
import { toast } from "sonner";
import { deleteDeck } from "../_server/deleteDeck";

interface Props {
	deck: Pick<Selectable<Decks>, "id" | "name">;
	openSignal: Signal<boolean>;
}

export const DeleteDeckAlert: React.FC<Props> = ({ deck, openSignal }) => {
	const queryClient = useQueryClient();

	const { mutate, isPending } = useMutation({
		mutationFn: async () => {
			await deleteDeck(deck.id);
		},

		onSuccess: () => {
			toast.success(`Deck ${deck.name} has been deleted, it shall be missed`);
		},

		onError: () => {
			toast.error(`Something went wrong while deleting ${deck.name}`);
		},

		onSettled: async () => {
			openSignal.value = false;
			await queryClient.invalidateQueries({
				queryKey: ["decks", "personal"],
			});
		},
	});

	return (
		<AlertDialog open={openSignal.value}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
					<AlertDialogDescription>
						This action cannot be undone. This will permanently delete the deck{" "}
						<strong>{deck.name}</strong> and remove it from our servers.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel
						onClick={(e) => {
							e.stopPropagation();
							openSignal.value = false;
						}}
					>
						Cancel
					</AlertDialogCancel>
					<Button
						variant="destructive"
						isLoading={isPending}
						onClick={(e) => {
							e.stopPropagation();
							mutate();
						}}
					>
						Delete deck
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};
